import { useState, useEffect, useMemo, useCallback } from "react";
import { Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, ArrowLeft, Edit, Trash2, Search, Eye, 
  Check, X, Clock, DollarSign, FileText, Download, Printer,
  Building, Calendar, FileCheck, AlertCircle, Wrench, Package
} from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function _abrirPdfProposta(orcId, modo = "download") {
  const token = sessionStorage.getItem("luvcorp_token");
  try {
    toast.loading("Gerando PDF...", { id: "pdf-gen" });
    await axios.post(`${API}/orcamentos/${orcId}/gerar-pdf`, {}, { headers: { Authorization: `Bearer ${token}` } });
    const resp = await fetch(`${BACKEND_URL}/api/orcamentos/${orcId}/pdf?inline=true`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!resp.ok) throw new Error("Falha ao carregar PDF");
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    toast.success("PDF pronto!", { id: "pdf-gen" });
    if (modo === "imprimir") {
      const w = window.open(url, "_blank");
      if (w) setTimeout(() => { try { w.print(); } catch (e) { console.warn("Print bloqueado:", e?.message); } }, 500);
    } else if (modo === "visualizar") {
      window.open(url, "_blank");
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = `proposta_${orcId.slice(0,8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (err) {
    toast.error("Erro ao gerar PDF", { id: "pdf-gen" });
  }
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

const statusConfig = {
  aguardando_aprovacao: { label: "Aguardando Aprovacao", class: "bg-amber-100 text-amber-700" },
  aprovado: { label: "Aprovado", class: "bg-green-100 text-green-700" },
  recusado: { label: "Recusado", class: "bg-red-100 text-red-700" },
};

const tipoItemConfig = {
  material: { label: "Material", class: "bg-blue-100 text-blue-700", icon: Package },
  mao_de_obra: { label: "Mao de Obra", class: "bg-purple-100 text-purple-700", icon: Wrench },
};

// ==================== LIST ====================
function OrcamentosList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const navigate = useNavigate();

  const fetchItems = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      const res = await axios.get(`${API}/orcamentos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(res.data);
    } catch (err) {
      toast.error("Erro ao carregar orcamentos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = !searchTerm || 
        item.numero_formatado?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.descricao?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === "all" || item.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [items, searchTerm, filterStatus]);

  const handleDelete = async (id) => {
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      await axios.delete(`${API}/orcamentos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Orcamento excluido");
      fetchItems();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao excluir"));
    }
  };

  if (loading) return <MainLayout><div className="p-8 text-center">Carregando...</div></MainLayout>;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="orcamentos-list">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Orcamentos</h1>
            <p className="text-slate-500">Propostas comerciais e orcamentos</p>
          </div>
          <Link to="/comercial/propostas/novo">
            <Button className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white" data-testid="new-orcamento-btn">
              <Plus className="h-4 w-4 mr-2" />
              Novo Orcamento
            </Button>
          </Link>
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar por numero, cliente ou descricao..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <NativeSelect
                value={filterStatus}
                onValueChange={setFilterStatus}
                placeholder="Status"
                options={[
                  { value: "all", label: "Todos" },
                  { value: "aguardando_aprovacao", label: "Aguardando Aprovacao" },
                  { value: "aprovado", label: "Aprovado" },
                  { value: "recusado", label: "Recusado" }
                ]}
                className="w-[200px]"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Numero</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Validade</TableHead>
                  <TableHead className="text-right">Acoes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      Nenhum orcamento encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map((item) => {
                    const status = statusConfig[item.status] || statusConfig.aguardando_aprovacao;
                    return (
                      <TableRow key={item.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/comercial/propostas/${item.id}`)}>
                        <TableCell className="font-mono font-bold text-luvcorp-navy">
                          {item.numero_formatado || `LCOPS${String(item.numero || 0).padStart(5, '0')}`}
                        </TableCell>
                        <TableCell>{formatDate(item.data_orcamento || item.created_at)}</TableCell>
                        <TableCell>{item.cliente_nome}</TableCell>
                        <TableCell className="text-right font-bold text-green-600">{formatCurrency(item.valor_total)}</TableCell>
                        <TableCell>
                          <Badge className={status.class}>{status.label}</Badge>
                        </TableCell>
                        <TableCell>{formatDate(item.data_validade)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/comercial/propostas/${item.id}`)} title="Visualizar" data-testid={`btn-view-${item.id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            {item.status === "aguardando_aprovacao" && (
                              <Button variant="ghost" size="sm" onClick={() => navigate(`/comercial/propostas/${item.id}/editar`)} title="Editar" data-testid={`btn-edit-${item.id}`}>
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => _abrirPdfProposta(item.id, "visualizar")} title="Ver PDF" data-testid={`btn-pdf-view-${item.id}`}>
                              <FileText className="h-4 w-4 text-[#0A1628]" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => _abrirPdfProposta(item.id, "download")} title="Baixar PDF" data-testid={`btn-pdf-dl-${item.id}`}>
                              <Download className="h-4 w-4 text-emerald-600" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => _abrirPdfProposta(item.id, "imprimir")} title="Imprimir" data-testid={`btn-pdf-print-${item.id}`}>
                              <Printer className="h-4 w-4 text-blue-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}

// ==================== FORM ====================
function OrcamentoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [centrosCusto, setCentrosCusto] = useState([]);
  const [contas, setContas] = useState([]);

  const [formData, setFormData] = useState({
    cliente_id: "",
    descricao: "",
    itens: [],
    data_orcamento: new Date().toISOString().split('T')[0], // Data do orçamento
    valor_sinal: 0,
    data_sinal: "",
    data_primeira_parcela: "",
    parcelas: 1,
    intervalo_parcelas: 30,
    validade_dias: 30,
    centro_custo_id: "",
    conta_id: "",
    observacoes: "",
    data_inicio_servico: "",
    prazo_execucao_dias: 30
  });

  // Item form
  const [itemForm, setItemForm] = useState({
    descricao: "",
    tipo_item: "material",
    quantidade: 1,
    valor_unitario: 0,
    unidade: "un"
  });

  useEffect(() => {
    const fetchData = async () => {
      const token = sessionStorage.getItem("luvcorp_token");
      try {
        const [clientesRes, ccRes, contasRes] = await Promise.all([
          axios.get(`${API}/clientes-fornecedores?tipo=cliente`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/centros-custo`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/plano-contas`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setClientes(clientesRes.data);
        setCentrosCusto(ccRes.data);
        setContas(contasRes.data.filter(c => c.tipo === "receita"));

        if (isEdit) {
          const res = await axios.get(`${API}/orcamentos/${id}`, { headers: { Authorization: `Bearer ${token}` } });
          setFormData({
            cliente_id: res.data.cliente_id || "",
            descricao: res.data.descricao || "",
            itens: res.data.itens || [],
            data_orcamento: res.data.data_orcamento?.split("T")[0] || res.data.created_at?.split("T")[0] || new Date().toISOString().split('T')[0],
            valor_sinal: res.data.valor_sinal || 0,
            data_sinal: res.data.data_sinal?.split("T")[0] || "",
            data_primeira_parcela: res.data.data_primeira_parcela?.split("T")[0] || "",
            parcelas: res.data.parcelas || 1,
            intervalo_parcelas: res.data.intervalo_parcelas || 30,
            validade_dias: res.data.validade_dias || 30,
            centro_custo_id: res.data.centro_custo_id || "",
            conta_id: res.data.conta_id || "",
            observacoes: res.data.observacoes || "",
            data_inicio_servico: res.data.data_inicio_servico?.split("T")[0] || "",
            prazo_execucao_dias: res.data.prazo_execucao_dias || 30
          });
        }
      } catch (err) {
        toast.error("Erro ao carregar dados");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEdit]);

  const addItem = () => {
    if (!itemForm.descricao || itemForm.valor_unitario <= 0) {
      toast.error("Preencha a descricao e o valor unitario");
      return;
    }
    setFormData({
      ...formData,
      itens: [...formData.itens, { ...itemForm, id: Date.now() }]
    });
    setItemForm({ descricao: "", tipo_item: "material", quantidade: 1, valor_unitario: 0, unidade: "un" });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      itens: formData.itens.filter((_, i) => i !== index)
    });
  };

  const totals = useMemo(() => {
    const maoDeObra = formData.itens
      .filter(i => i.tipo_item === "mao_de_obra")
      .reduce((sum, i) => sum + (i.quantidade * i.valor_unitario), 0);
    const material = formData.itens
      .filter(i => i.tipo_item === "material")
      .reduce((sum, i) => sum + (i.quantidade * i.valor_unitario), 0);
    return { maoDeObra, material, total: maoDeObra + material };
  }, [formData.itens]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.cliente_id) {
      toast.error("Selecione um cliente");
      return;
    }
    if (formData.itens.length === 0) {
      toast.error("Adicione pelo menos um item");
      return;
    }

    setSaving(true);
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      const payload = {
        ...formData,
        itens: formData.itens.map(({ id, ...rest }) => rest) // Remove temp IDs
      };

      if (isEdit) {
        await axios.put(`${API}/orcamentos/${id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Orcamento atualizado");
      } else {
        await axios.post(`${API}/orcamentos`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Orcamento criado");
      }
      navigate("/comercial/propostas");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao salvar"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <MainLayout><div className="p-8 text-center">Carregando...</div></MainLayout>;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="orcamento-form">
        <div className="flex items-center gap-4">
          <Link to="/comercial/propostas">
            <Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-2" />Voltar</Button>
          </Link>
          <div>
            <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">
              {isEdit ? "Editar Orcamento" : "Novo Orcamento"}
            </h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Cliente e Datas */}
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle className="text-lg">Dados Gerais</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Cliente *</Label>
                  <NativeSelect
                    value={formData.cliente_id}
                    onValueChange={(v) => setFormData({...formData, cliente_id: v})}
                    placeholder="Selecione o cliente"
                    options={clientes.map(c => ({ value: c.id, label: c.nome }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data do Orcamento *</Label>
                  <Input
                    type="date"
                    value={formData.data_orcamento}
                    onChange={(e) => setFormData({...formData, data_orcamento: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Validade (dias)</Label>
                  <Input
                    type="number"
                    value={formData.validade_dias}
                    onChange={(e) => setFormData({...formData, validade_dias: parseInt(e.target.value) || 30})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Inicio Previsto do Servico</Label>
                  <Input
                    type="date"
                    value={formData.data_inicio_servico}
                    onChange={(e) => setFormData({...formData, data_inicio_servico: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prazo de Execucao (dias)</Label>
                  <Input
                    type="number"
                    value={formData.prazo_execucao_dias}
                    onChange={(e) => setFormData({...formData, prazo_execucao_dias: parseInt(e.target.value) || 30})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descricao</Label>
                <Textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                  placeholder="Descricao geral do orcamento"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Itens */}
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle className="text-lg">Itens do Orcamento</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {/* Add Item Form */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label>Descricao</Label>
                    <Input
                      value={itemForm.descricao}
                      onChange={(e) => setItemForm({...itemForm, descricao: e.target.value})}
                      placeholder="Descricao do item"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <NativeSelect
                      value={itemForm.tipo_item}
                      onValueChange={(v) => setItemForm({...itemForm, tipo_item: v})}
                      options={[
                        { value: "material", label: "Material" },
                        { value: "mao_de_obra", label: "Mao de Obra" }
                      ]}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Qtd</Label>
                    <Input
                      type="number"
                      value={itemForm.quantidade}
                      onChange={(e) => setItemForm({...itemForm, quantidade: parseFloat(e.target.value) || 1})}
                      min="0.01"
                      step="0.01"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Valor Unit.</Label>
                    <Input
                      type="number"
                      value={itemForm.valor_unitario}
                      onChange={(e) => setItemForm({...itemForm, valor_unitario: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button type="button" onClick={addItem} className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark w-full">
                      <Plus className="h-4 w-4 mr-1" />Adicionar
                    </Button>
                  </div>
                </div>
              </div>

              {/* Items List */}
              {formData.itens.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Descricao</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Qtd</TableHead>
                      <TableHead className="text-right">Valor Unit.</TableHead>
                      <TableHead className="text-right">Subtotal</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.itens.map((item, idx) => {
                      const tipo = tipoItemConfig[item.tipo_item] || tipoItemConfig.material;
                      return (
                        <TableRow key={item.id || idx}>
                          <TableCell>{item.descricao}</TableCell>
                          <TableCell><Badge className={tipo.class}>{tipo.label}</Badge></TableCell>
                          <TableCell className="text-right">{item.quantidade}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.valor_unitario)}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(item.quantidade * item.valor_unitario)}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => removeItem(idx)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-80 space-y-2 p-4 bg-slate-50 rounded-lg">
                  <div className="flex justify-between text-purple-600">
                    <span>Total Mao de Obra:</span>
                    <span className="font-medium">{formatCurrency(totals.maoDeObra)}</span>
                  </div>
                  <div className="flex justify-between text-blue-600">
                    <span>Total Material:</span>
                    <span className="font-medium">{formatCurrency(totals.material)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-lg font-bold text-luvcorp-navy">
                    <span>TOTAL GERAL:</span>
                    <span>{formatCurrency(totals.total)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pagamento */}
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle className="text-lg">Condicoes de Pagamento</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Sinal/Entrada</Label>
                  <Input
                    type="number"
                    value={formData.valor_sinal}
                    onChange={(e) => setFormData({...formData, valor_sinal: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data do Sinal</Label>
                  <Input
                    type="date"
                    value={formData.data_sinal}
                    onChange={(e) => setFormData({...formData, data_sinal: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Parcelas</Label>
                  <Input
                    type="number"
                    value={formData.parcelas}
                    onChange={(e) => setFormData({...formData, parcelas: parseInt(e.target.value) || 1})}
                    min="1"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Intervalo (dias)</Label>
                  <Input
                    type="number"
                    value={formData.intervalo_parcelas}
                    onChange={(e) => setFormData({...formData, intervalo_parcelas: parseInt(e.target.value) || 30})}
                    min="1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Data Primeira Parcela</Label>
                <Input
                  type="date"
                  value={formData.data_primeira_parcela}
                  onChange={(e) => setFormData({...formData, data_primeira_parcela: e.target.value})}
                  className="max-w-xs"
                />
              </div>
            </CardContent>
          </Card>

          {/* Contabil */}
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle className="text-lg">Classificacao Contabil</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Centro de Custo</Label>
                  <NativeSelect
                    value={formData.centro_custo_id}
                    onValueChange={(v) => setFormData({...formData, centro_custo_id: v})}
                    placeholder="Selecione..."
                    options={centrosCusto.map(c => ({ value: c.id, label: `${c.codigo} - ${c.nome}` }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Conta Contabil</Label>
                  <NativeSelect
                    value={formData.conta_id}
                    onValueChange={(v) => setFormData({...formData, conta_id: v})}
                    placeholder="Selecione..."
                    options={contas.map(c => ({ value: c.id, label: `${c.codigo} - ${c.nome}` }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Observacoes</Label>
                <Textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  placeholder="Observacoes adicionais"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Link to="/comercial/propostas">
              <Button variant="outline" type="button">Cancelar</Button>
            </Link>
            <Button type="submit" disabled={saving} className="bg-luvcorp-navy hover:bg-luvcorp-navy-dark">
              {saving ? "Salvando..." : (isEdit ? "Atualizar" : "Criar Orcamento")}
            </Button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
}

// ==================== VIEW ====================
function OrcamentoView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const token = sessionStorage.getItem("luvcorp_token");
        const res = await axios.get(`${API}/orcamentos/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        setItem(res.data);
      } catch (err) {
        toast.error("Erro ao carregar orcamento");
        navigate("/comercial/propostas");
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [id, navigate]);

  const handleApprove = async () => {
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      const res = await axios.post(`${API}/orcamentos/${id}/aprovar`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Orcamento aprovado! OS ${res.data.ordem_servico_numero} criada.`);
      navigate("/comercial/propostas");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao aprovar"));
    }
  };

  const handleReject = async () => {
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      await axios.post(`${API}/orcamentos/${id}/recusar`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Orcamento recusado");
      navigate("/comercial/propostas");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao recusar"));
    }
  };

  if (loading) return <MainLayout><div className="p-8 text-center">Carregando...</div></MainLayout>;
  if (!item) return null;

  const status = statusConfig[item.status] || statusConfig.aguardando_aprovacao;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="orcamento-view">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/comercial/propostas">
              <Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-2" />Voltar</Button>
            </Link>
            <div>
              <h1 className="text-2xl font-heading font-bold text-luvcorp-navy font-mono">
                {item.numero_formatado}
              </h1>
              <p className="text-slate-500">{item.cliente?.nome}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`${status.class} text-sm px-3 py-1`}>{status.label}</Badge>
            <Button variant="outline" size="sm" onClick={() => _abrirPdfProposta(item.id, "visualizar")} className="border-[#0A1628] text-[#0A1628]" data-testid="btn-view-pdf">
              <FileText className="h-4 w-4 mr-1" />Ver PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => _abrirPdfProposta(item.id, "download")} className="border-emerald-600 text-emerald-600" data-testid="btn-download-pdf">
              <Download className="h-4 w-4 mr-1" />Baixar
            </Button>
            <Button variant="outline" size="sm" onClick={() => _abrirPdfProposta(item.id, "imprimir")} className="border-blue-600 text-blue-600" data-testid="btn-print-pdf">
              <Printer className="h-4 w-4 mr-1" />Imprimir
            </Button>
            {item.status === "aguardando_aprovacao" && (
              <>
                <Button variant="outline" size="sm" onClick={() => navigate(`/comercial/propostas/${id}/editar`)} className="border-[#0A1628]" data-testid="btn-edit">
                  <Edit className="h-4 w-4 mr-1" />Editar
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowRejectDialog(true)} className="text-red-600 border-red-200 hover:bg-red-50">
                  <X className="h-4 w-4 mr-1" />Recusar
                </Button>
                <Button size="sm" onClick={() => setShowApproveDialog(true)} className="bg-green-600 hover:bg-green-700">
                  <Check className="h-4 w-4 mr-1" />Aprovar
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg"><Wrench className="h-5 w-5 text-purple-600" /></div>
                <div>
                  <p className="text-sm text-slate-500">Mao de Obra</p>
                  <p className="text-lg font-bold text-purple-600">{formatCurrency(item.total_mao_de_obra)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg"><Package className="h-5 w-5 text-blue-600" /></div>
                <div>
                  <p className="text-sm text-slate-500">Material</p>
                  <p className="text-lg font-bold text-blue-600">{formatCurrency(item.total_material)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg"><DollarSign className="h-5 w-5 text-green-600" /></div>
                <div>
                  <p className="text-sm text-slate-500">Total</p>
                  <p className="text-lg font-bold text-green-600">{formatCurrency(item.valor_total)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg"><Calendar className="h-5 w-5 text-amber-600" /></div>
                <div>
                  <p className="text-sm text-slate-500">Validade</p>
                  <p className="text-lg font-bold text-amber-600">{formatDate(item.data_validade)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Itens */}
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle>Itens do Orcamento</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Descricao</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Qtd</TableHead>
                  <TableHead className="text-right">Valor Unit.</TableHead>
                  <TableHead className="text-right">Subtotal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(item.itens || []).map((it, idx) => {
                  const tipo = tipoItemConfig[it.tipo_item] || tipoItemConfig.material;
                  return (
                    <TableRow key={idx}>
                      <TableCell>{it.descricao}</TableCell>
                      <TableCell><Badge className={tipo.class}>{tipo.label}</Badge></TableCell>
                      <TableCell className="text-right">{it.quantidade}</TableCell>
                      <TableCell className="text-right">{formatCurrency(it.valor_unitario)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(it.quantidade * it.valor_unitario)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle>Execucao</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between"><span className="text-slate-500">Inicio Previsto:</span><span>{formatDate(item.data_inicio_servico)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Prazo:</span><span>{item.prazo_execucao_dias} dias</span></div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle>Pagamento</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between"><span className="text-slate-500">Sinal:</span><span>{formatCurrency(item.valor_sinal)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Parcelas:</span><span>{item.parcelas}x</span></div>
            </CardContent>
          </Card>
        </div>

        {/* Approve Dialog */}
        <AlertDialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Aprovar Orcamento?</AlertDialogTitle>
              <AlertDialogDescription>
                Ao aprovar, sera gerado automaticamente:
                <ul className="list-disc ml-6 mt-2">
                  <li>Ordem de Servico com numero {item.numero_formatado}</li>
                  <li>Lancamentos no Contas a Receber</li>
                </ul>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleApprove} className="bg-green-600 hover:bg-green-700">Aprovar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reject Dialog */}
        <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Recusar Orcamento?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acao nao pode ser desfeita. O orcamento sera marcado como recusado.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleReject} className="bg-red-600 hover:bg-red-700">Recusar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}

// ==================== MAIN ====================
export default function OrcamentosPage() {
  return (
    <Routes>
      <Route index element={<OrcamentosList />} />
      <Route path="novo" element={<OrcamentoForm />} />
      <Route path=":id" element={<OrcamentoView />} />
      <Route path=":id/editar" element={<OrcamentoForm />} />
    </Routes>
  );
}
